/**
 * Swatch Group Card Manager
 *
 * Handles interactive swatch and hardware filtering for swatch-group collection cards.
 * All data pre-rendered by Liquid - this only handles click events and DOM updates.
 *
 * @version 1.0.0
 */
(function () {
  'use strict';

  const MAX_VISIBLE_SWATCHES = 5;
  const DEBUG = false;

  function log(...args) {
    if (DEBUG) {
      console.log('[Swatch Group Manager]', ...args);
    }
  }

  /**
   * Cache DOM selectors for a card
   * @param {HTMLElement} cardWrapper - The card wrapper element
   * @returns {Object} Cache object with DOM references
   */
  function initCardCache(cardWrapper) {
    const cardId = cardWrapper.dataset.productGroup;
    return {
      colourText: cardWrapper.querySelector('.current-product-text'),
      mainImage: cardWrapper.querySelector('img[id^="main-product-image-"]'),
      titleLink: cardWrapper.querySelector('a[id^="title-link-"]'),
      cardMedia: cardWrapper.querySelector('.card__media'),
      priceDisplay: cardWrapper.querySelector('.product-price-inline'),
      productsGrid: cardWrapper.querySelector('.products-grid'),
      swatchOverflow: cardWrapper.querySelector('.swatch-overflow'),
      configData: cardWrapper.querySelector('.swatch-config-data'),
      variantInput: document.getElementById('variant-id-' + cardId),
      addButton: document.getElementById('quick-add-button-' + cardId),
      overlayVariantInput: document.getElementById('overlay-variant-id-' + cardId),
      overlayButton: document.getElementById('overlay-quick-add-button-' + cardId),
    };
  }

  /**
   * Get cached selectors or initialize cache
   * @param {HTMLElement} cardWrapper - The card wrapper element
   * @returns {Object} Cache object with DOM references
   */
  function getCache(cardWrapper) {
    if (!cardWrapper._elemCache) {
      cardWrapper._elemCache = initCardCache(cardWrapper);
    }
    return cardWrapper._elemCache;
  }

  /**
   * Format price for display
   * @param {number|string} priceInCents - Price in cents/pence
   * @returns {string} Formatted price string
   */
  function formatPrice(priceInCents) {
    const price = parseInt(priceInCents, 10) / 100;
    if (isNaN(price)) return '';
    if (price % 1 === 0) {
      return '£' + price.toFixed(0);
    }
    return '£' + price.toFixed(2);
  }

  /**
   * Update card display with new product data
   * @param {HTMLElement} cardWrapper - The card wrapper element
   * @param {HTMLElement} productWrapper - The clicked swatch's product wrapper
   */
  function updateCardDisplay(cardWrapper, productWrapper) {
    const data = productWrapper.dataset;
    const cache = getCache(cardWrapper);

    log('Updating card display:', { cardId: cardWrapper.dataset.productGroup, colour: data.colour, hardware: data.hardware });

    cardWrapper.querySelectorAll('.product-swatch').forEach(function (s) {
      s.classList.remove('active');
    });
    productWrapper.querySelector('.product-swatch').classList.add('active');

    if (cache.colourText) {
      cache.colourText.textContent = data.colour || '';
      cache.colourText.dataset.originalText = data.colour || '';
    }

    if (cache.mainImage && data.image) {
      cache.mainImage.src = data.image;
      cache.mainImage.srcset = data.image + ' 600w';
    }

    if (cache.titleLink && data.url) {
      cache.titleLink.href = data.url;
    }

    if (cache.cardMedia && data.url) {
      cache.cardMedia.onclick = function () {
        window.location.href = data.url;
      };
    }

    if (cache.priceDisplay) {
      const price = data.price;
      const comparePrice = data.comparePrice;

      var html = '<div class="price';
      if (
        comparePrice &&
        comparePrice !== 'null' &&
        comparePrice !== '' &&
        parseInt(comparePrice, 10) > parseInt(price, 10)
      ) {
        html += ' price--on-sale">';
        html +=
          '<span class="price-item price-item--regular">' + formatPrice(comparePrice) + '</span> ';
        html +=
          '<span class="price-item price-item--sale price-item--last">' +
          formatPrice(price) +
          '</span>';
      } else {
        html += '">';
        html +=
          '<span class="price-item price-item--regular price-item--last">' +
          formatPrice(price) +
          '</span>';
      }
      html += '</div>';
      cache.priceDisplay.innerHTML = html;
    }

    const available = data.available === 'true';
    const variantId = data.variantId;

    if (cache.variantInput) {
      cache.variantInput.value = variantId;
      cache.variantInput.disabled = !available;
    }

    if (cache.addButton) {
      cache.addButton.disabled = !available;
      const buttonText = cache.addButton.querySelector('span:not(.sold-out-message)');
      if (buttonText) {
        buttonText.textContent = available ? 'ADD TO BAG' : 'SOLD OUT';
      }
    }

    if (cache.overlayVariantInput) {
      cache.overlayVariantInput.value = variantId;
      cache.overlayVariantInput.disabled = !available;
    }

    if (cache.overlayButton) {
      cache.overlayButton.disabled = !available;
    }
  }

  /**
   * Update swatch visibility based on hardware filter
   * @param {HTMLElement} cardWrapper - The card wrapper element
   * @param {string} hardware - 'Gold' or 'Silver'
   */
  function filterByHardware(cardWrapper, hardware) {
    const cache = getCache(cardWrapper);
    if (!cache.productsGrid) return;

    log('Filtering by hardware:', hardware);

    const allWrappers = cache.productsGrid.querySelectorAll('.product-wrapper');
    var visibleCount = 0;
    var firstVisible = null;

    allWrappers.forEach(function (wrapper) {
      const wrapperHardware = wrapper.dataset.hardware;
      const matches = wrapperHardware === hardware;

      wrapper.classList.remove('hardware-visible', 'swatch-hidden-overflow');

      if (matches) {
        wrapper.classList.add('hardware-visible');
        if (visibleCount < MAX_VISIBLE_SWATCHES) {
          if (!firstVisible) firstVisible = wrapper;
          visibleCount++;
        } else {
          wrapper.classList.add('swatch-hidden-overflow');
        }
      }
    });

    if (cache.swatchOverflow) {
      const overflowAttr =
        hardware === 'Gold' ? 'data-gold-overflow' : 'data-silver-overflow';
      const overflowCount = parseInt(cache.swatchOverflow.getAttribute(overflowAttr), 10) || 0;

      if (overflowCount > 0) {
        cache.swatchOverflow.textContent = '+' + overflowCount;
        cache.swatchOverflow.classList.add('show');
      } else {
        cache.swatchOverflow.textContent = '';
        cache.swatchOverflow.classList.remove('show');
      }
    }

    cardWrapper.querySelectorAll('.hardware-filter-swatch').forEach(function (btn) {
      if (btn.dataset.hardware === hardware) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    cache.productsGrid.dataset.currentHardware = hardware;

    if (firstVisible) {
      updateCardDisplay(cardWrapper, firstVisible);
    }

    if (cache.configData) {
      try {
        const config = JSON.parse(cache.configData.textContent);
        if (config.isHardwareOnly && cache.colourText) {
          cache.colourText.textContent = hardware;
          cache.colourText.dataset.originalText = hardware;
        }
      } catch (e) {
        log('Error parsing config:', e);
      }
    }
  }

  /**
   * Apply configuration classes to card wrapper
   * @param {HTMLElement} cardWrapper - The card wrapper element
   */
  function applyConfigClasses(cardWrapper) {
    const cache = getCache(cardWrapper);
    if (!cache.configData) return;

    try {
      const config = JSON.parse(cache.configData.textContent);

      log('Applying config classes:', {
        card: cardWrapper.dataset.productGroup,
        config: config,
      });

      if (config.isHardwareOnly) {
        cardWrapper.classList.add('hardware-only');
      }
      if (config.hasNoHardware) {
        cardWrapper.classList.add('no-hardware');
      }
      if (config.hasSingleHardware) {
        cardWrapper.classList.add('single-hardware');
      }
      if (!config.hasColours) {
        cardWrapper.classList.add('no-colours');
      }

      if (config.isHardwareOnly && cache.colourText) {
        cache.colourText.textContent = config.currentHardware || 'Gold';
        cache.colourText.dataset.originalText = config.currentHardware || 'Gold';
      }
    } catch (e) {
      log('Error parsing config:', e);
    }
  }

  /**
   * Initialize event delegation for all swatch-group cards
   */
  function init() {
    log('Initializing swatch group card manager');

    const cards = document.querySelectorAll('[data-product-group]');
    log('Found', cards.length, 'swatch-group cards');

    cards.forEach(applyConfigClasses);

    document.addEventListener('click', function (e) {
      if (
        e.target.closest('.quick-add__submit') ||
        e.target.closest('.overlay-add-button')
      ) {
        return;
      }

      if (e.target.classList.contains('product-swatch')) {
        const cardWrapper = e.target.closest('.product-card-wrapper[data-product-group]');
        if (!cardWrapper) return;

        const productWrapper = e.target.closest('.product-wrapper');
        if (productWrapper) {
          log('Swatch clicked:', productWrapper.dataset.colour);
          updateCardDisplay(cardWrapper, productWrapper);
        }
      }

      if (e.target.classList.contains('hardware-filter-swatch')) {
        e.preventDefault();
        e.stopPropagation();

        if (e.target.disabled) return;

        const cardWrapper = e.target.closest('.product-card-wrapper[data-product-group]');
        if (!cardWrapper) return;

        const hardware = e.target.dataset.hardware;
        log('Hardware filter clicked:', hardware);
        filterByHardware(cardWrapper, hardware);
      }
    });

    document.addEventListener(
      'mouseover',
      function (e) {
        if (e.target.classList.contains('product-swatch')) {
          const cardWrapper = e.target.closest('.product-card-wrapper[data-product-group]');
          if (!cardWrapper) return;

          const productWrapper = e.target.closest('.product-wrapper');
          const cache = getCache(cardWrapper);

          if (productWrapper && cache.colourText) {
            if (!cache.colourText.dataset.originalText) {
              cache.colourText.dataset.originalText = cache.colourText.textContent;
            }
            cache.colourText.textContent = productWrapper.dataset.colour || '';
          }
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'mouseout',
      function (e) {
        if (e.target.classList.contains('product-swatch')) {
          const cardWrapper = e.target.closest('.product-card-wrapper[data-product-group]');
          if (!cardWrapper) return;

          const cache = getCache(cardWrapper);
          if (cache.colourText && cache.colourText.dataset.originalText) {
            cache.colourText.textContent = cache.colourText.dataset.originalText;
          }
        }
      },
      { passive: true }
    );

    log('Initialization complete');
  }

  /**
   * Initialize when browser is idle to avoid blocking main thread
   */
  function initWhenIdle() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 100 });
    } else {
      // Fallback for Safari
      setTimeout(init, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenIdle);
  } else {
    initWhenIdle();
  }
})();
